import type { UserBookProgressFragment } from "./generated";
import { api } from "./generated";

/**
 * AIDEV-NOTE: Mirror of practiceQuizAttemptApi for the learned/consolidated/tested flags. The
 * `userBookProgressSet` mutation returns the whole new document and it is written straight into
 * BOTH caches that read progress: the per-book `getUserBookProgress` (book detail, quiz) and the
 * batch `getUserBookProgressAll` ("My books" overview). The api has no tagTypes, so without this
 * the overview kept serving the stale response, with no refetch, after "Learned" was clicked on
 * the detail and the student came back within a minute (default `keepUnusedDataFor`) —
 * `pickRandomExamBook` had nothing to draw from and "Random exam" stayed disabled. Patching a
 * missing cache entry is a no-op; the entry is then fetched whole on the next mount, as before.
 * No extra request. Hooks are imported from here, not from api/generated, so the enhancement
 * always runs before first use. [TD-380]
 */
const upsertProgress = (bookId: string, progress: UserBookProgressFragment) =>
  userBookProgressApi.util.updateQueryData("getUserBookProgress", { bookId }, draft => {
    draft.userBookProgress = progress;
  });

// The overview selects `grade` and not `flips`, so only the flags are overwritten — replacing
// the whole item with the fragment would drop the grade from the cache. A book without a record
// is missing from the overview (the server creates the document on first write), so it gets
// added; it earns a grade only by finishing an exam.
const upsertProgressInAll = (progress: UserBookProgressFragment) =>
  userBookProgressApi.util.updateQueryData("getUserBookProgressAll", undefined, draft => {
    const { id, bookId, hasLearned, hasConsolidated, hasTested } = progress;
    const item = draft.userBookProgressAll.find(entry => entry.bookId === bookId);

    if (item === undefined) {
      draft.userBookProgressAll.push({ id, bookId, hasLearned, hasConsolidated, hasTested, grade: null });
      return;
    }

    item.hasLearned = hasLearned;
    item.hasConsolidated = hasConsolidated;
    item.hasTested = hasTested;
  });

export const userBookProgressApi = api.enhanceEndpoints({
  endpoints: {
    setUserBookProgress: {
      async onQueryStarted({ bookId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(upsertProgress(bookId, data.userBookProgressSet));
          dispatch(upsertProgressInAll(data.userBookProgressSet));
        } catch {
          // The caller's mutation error state reports the failure — the cache is left alone.
        }
      },
    },
  },
});

export const { useGetUserBookProgressQuery, useGetUserBookProgressAllQuery, useSetUserBookProgressMutation } =
  userBookProgressApi;
